import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import { Button } from '@/components/ui/button';
import { GraduationCap, CalendarCheck, CalendarClock, IndianRupee, History, Star, LifeBuoy, LogOut } from 'lucide-react';
import ChangePasswordDialog from './ChangePasswordDialog';
import StudentNotificationBell from './StudentNotificationBell';
import { apiError } from '../lib/apiError';

const NAV = [
  ['/teacher-portal', 'Upcoming', CalendarCheck, 'teacher-nav-upcoming'],
  ['/teacher-portal/availability', 'Availability', CalendarClock, 'teacher-nav-availability'],
  ['/teacher-portal/pricing', 'Pricing', IndianRupee, 'teacher-nav-pricing'],
  ['/teacher-portal/history', 'History', History, 'teacher-nav-history'],
  ['/teacher-portal/ratings', 'Ratings', Star, 'teacher-nav-ratings'],
  ['/teacher-portal/support', 'Support', LifeBuoy, 'teacher-nav-support'],
];

const TeacherLayout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState('');

  const reload = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/teacher-portal/me`);
      setProfile(data);
    } catch (e) {
      setError(apiError(e, 'Could not load your profile'));
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  return (
    <div className="min-h-screen bg-slate-50">
      <nav className="bg-slate-900 sticky top-0 z-50 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap items-center justify-between gap-3 py-3">
          <Link to="/teacher-portal" className="flex items-center gap-3" data-testid="teacher-logo-link">
            <div className="w-10 h-10 rounded-xl bg-violet-500 flex items-center justify-center">
              <GraduationCap className="w-6 h-6 text-white" />
            </div>
            <div className="leading-tight">
              <div className="text-xl font-bold text-white">WISE Teacher</div>
              <div className="text-xs text-slate-400" data-testid="teacher-user-name">{user?.name}</div>
            </div>
          </Link>
          <div className="flex flex-1 flex-wrap items-center justify-end gap-1.5">
            {NAV.map(([to, label, Icon, testId]) => {
              const active = location.pathname === to;
              return (
                <Button key={to} asChild variant="ghost" data-testid={testId}
                  className={active ? 'bg-violet-500 text-white hover:bg-violet-600 hover:text-white' : 'text-slate-300 hover:bg-white/10 hover:text-white'}>
                  <Link to={to}><Icon className="w-4 h-4 mr-2" />{label}</Link>
                </Button>
              );
            })}
            <div className="flex items-center gap-1 border-l border-white/15 pl-2 ml-1 bg-white rounded-full">
              <StudentNotificationBell />
              <ChangePasswordDialog />
              <Button variant="ghost" size="sm" onClick={logout} className="rounded-full" data-testid="teacher-logout-button" aria-label="Log out" title="Log out">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </nav>
      <main className="py-8">
        {error ? (
          <p className="text-center text-slate-500 py-20" data-testid="teacher-profile-error">{error}</p>
        ) : profile ? (
          <Outlet context={{ profile, reload }} />
        ) : (
          <p className="text-center text-slate-500 py-20">Loading...</p>
        )}
      </main>
    </div>
  );
};

export default TeacherLayout;
